import {BaseElementAsync, BASIS} from "/@back@/commons/basis.js";
import {ActionBtn, Button} from "/@back@/commons/widgets/buttons.js";
import {POPUP, PopupTooltip} from "/@back@/commons/widgets/popups.js";
import {ShowOutlineAction} from "/@back@/edit/wed/features/outlineBar.js";
import {ShowPreviewAction} from "/@back@/edit/wed/features/previewBar.js";
import {WED} from "/@back@/edit/wed/wedCore.js";
import {IWedEditor, OWedManagerConfig} from "/@back@/edit/wed/wedEditor.js";
import {OWedEditorBoxConfig, WedEditorBox} from "/@back@/edit/wed/wedEditorBox.js";
import {boxTagsDefined} from "/@back@/edit/wed/wedlets/box/boxTags.js";
import {ITxtAnnotDiff, ITxtRefresh, TxtRoot, txtTagsDefined} from "/@back@/edit/wed/wedlets/txt/txt.js";
import {InlLink, TxtElement, TxtObject} from "/@back@/edit/wed/wedlets/txt/txtTags.js";
import {IItemUiEnv, isItemUiEnv} from "/@back@/wsp/views/itemMain.js";
import {SrcDrawer, SRCDRAWER} from "/@back@/wsp/widgets/srcDrawer.js";
import {ptrItemTagsDefined, sgnPatternFromTxtElement, TxtPtrItem, TxtPtrItemBlock, TxtPtrItemInline, WspDataTransferWedAnalyzer} from "/@back@/wsp/widgets/wed/ptrItem.js";
import {Action, EButtonUiContext} from "/@lib@/commons/actions.js";
import {EHttpStatusCode, RespError} from "/@lib@/commons/io/io.js";
import {JLastDatas} from "/@lib@/commons/lastDatas.js";
import {IReg, IUiEnv, REG} from "/@lib@/commons/registry.js";
import {SEC} from "/@lib@/commons/security.js";
import {IView, IViewContainer, OViewVisitOptions, VIEWS, ViewsContainer} from "/@lib@/commons/views.js";
import {XA} from "/@lib@/commons/xml/xAddr.js";
import {IChainEnv} from "/@lib@/wsp/chain.js";
import {InfoFocusItem, InfoFocusNodeInItem, InfoHighlighItemSgn, ITEM} from "/@lib@/wsp/item.js";
import {JSrcFields, SRC, srcRef} from "/@lib@/wsp/src.js";
import {IWspEnv, IWspUiEnv, WSP, wspCd} from "/@lib@/wsp/wsp.js";
import {EWspWorkingSt, IPlaceDocHolder, IWspsLivePlacePointer} from "/@lib@/wsp/wspsLive.js";
import {EItemTypeFamily, IDatasEditor, IItemTypeUiEnv, ItemType} from "/@lib@/wsp/wspMetaUi.js";
import {ItemCreator} from "/@back@/wsp/dialogs/itemCreator.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {InsertScComment} from "/@back@/edit/wed/wedlets/box/boxScComment.js";
import {IInfo, IInfoBroker, IInfoBrokerPointer, IInfoProducer, InfoBrokerBasic} from "/@lib@/commons/infos.js";
import {IPopupable} from "/@back@/commons/widgets/popupable.js";
import {WEDLET} from "/@back@/edit/wed/wedlets/wedlet.js";
import {FastFindItem, OFastFindItemsInit} from "/@back@/wsp/dialogs/fastFindItem.js";
import {SearchItemSgnRegexp} from "/@lib@/wsp/search.js";
import {TxtSelMgr} from "/@back@/edit/wed/features/txtSel.js";
import {IPtrItemRule} from "/@back@/wsp/widgets/wed/schemaMetaWsp.js";
import "/@back@/wsp/widgets/wed/itemEditorExt.js";
import "/@back@/wsp/widgets/wed/wspSearchBar.js";
import {IDiffAnnotValue, ISkDiffAnnot} from "/@lib@/edit/schema/diff.js";
import {IWedDiffTxtObjCustom, WedDiffTxtObj} from "/@back@/edit/wed/wedlets/diff/diffTags.js";
import {ItemViewerSingle} from "/@back@/wsp/views/itemViewerSingle.js";
import {HistoEditAction, HistoEditPoint, IHistoEditEnv, IHistoEditHolder, isHistoEditEnv} from "/@lib@/edit/histoEditPoints.js";

/**
 * View d'édition d'un (et d'un seul) item.
 *
 * Peut-être utilisé dans de nombreux contextes. Le registre environnant doit être au minimum de type IChainEnv,
 * un sous-registre sera instancié automatiquement pour compléter la configuration.
 *
 * Config attributes:
 * - wsp --> wspCd
 * - src-ref --> srcRef
 * - editor-key --> editorKey
 * - editor-config (json) --> editorConfig
 * - wedmgr-config (json) --> wedMgrConfig
 */
export interface OItemXmlEdInit {
	reg?: IReg<IChainEnv & IUiEnv> | IReg<IWspUiEnv> | IReg<IItemTypeUiEnv> | IReg<IItemUiEnv>
	wspCd?: wspCd
	srcRef?: srcRef
	editorKey?: string
	editorConfig?: OWedEditorBoxConfig
	wedMgrConfig?: OWedManagerConfig
	writePerms?: string | string[]
	datasEditor?: IDatasEditor
	noNotifIfReadOnly?: boolean
	initItemReg?: (reg: IReg<IItemUiEnv>, itemEditor: ItemXmlEd) => void
	saveOnClose?: boolean
	/** lastDatas pour l'initialisation (la key est construite auto: "ed:"+srcRef). */
	lastDatas?: JLastDatas
}

export interface ItemXmlEd extends BaseElementAsync {
	initialize(init: OItemXmlEdInit): this;
}

export class ItemXmlEd extends BaseElementAsync implements IView, IViewContainer {

	/**
	 * L'env de l'éditeur doit être de niveau IItemUiEnv en particulier pour le securityCtx
	 * et l'eval des perms qui pourraient être réalisées dans le wed.
	 */
	reg: IReg<IItemUiEnv>;

	configs: OItemXmlEdInit;

	view: WedEditorBox;

	protected _saveAct: SaveAction;

	isClosed(): boolean {return !this.view}

	/**
	 * @param leaveState Si la place est partagée (non instanciée dans l'itemEditor),
	 *    passe l'item édité à l'état leaveState (généralement r(ead) ou l(isten)).
	 */
	closeEditor(leaveState?: EWspWorkingSt) {
		if (!this.view) return;
		VIEWS.onViewHidden(this.view, true);
		if (this.configs.saveOnClose && this.isDirty()) this.save();
		this.view.wedMgr.killEditor();
		if ((this.configs.reg.env as IWspsLivePlacePointer).place !== this.reg.env.place) {
			//On a créé notre place en interne.
			this.reg.env.place.closePlace();
			this.reg.env.place = null;
		} else {
			//place partagée.
			if (leaveState != null) this.reg.env.place.setState(WSP.buildWspRef(this.configs.wspCd, this.configs.srcRef), leaveState);
		}
		this.view = null;
	}

	_refresh() {
		super._refresh();
		if (this.view) {
			const wedMgr = this.view.wedMgr;
			const docHolder = wedMgr.docHolder as IPlaceDocHolder;
			if (docHolder.isAvailable) {
				if (!wedMgr.setReadOnly('locked', docHolder.house.lockedBy != null, 'perms', !this.reg.hasPerm(this._writePermToCheck()))) {
					//Le Refresh peut être dû à un chgt de roles qui peuvent avoir un impact uniquement au sein de l'éditeur
					WEDLET.refreshEditMode(wedMgr.rootWedlet);
				}
			}
		}
	}

	protected _writePermToCheck() {
		return this.configs.writePerms || "action.itemXmlEd#edit";
	}

	isDirty(): boolean {
		return this.view ? this.view.wedMgr.docHolder.house.isDirty : false;
	}

	async save(): Promise<void> {
		return this._saveAct.execute(this.view);
	}

	protected _initialize(init: OItemXmlEdInit): Promise<void> {
		this.style.display = "contents";
		this.configs = init || {};
		if (!this.configs.reg) this.configs.reg = this.findReg(init);
		return this.launchEditor();
	}

	buildInitFromAtts(init: OItemXmlEdInit): OItemXmlEdInit {
		init = super.buildInitFromAtts(init);
		if (this.hasAttribute('wsp')) init.wspCd = BASIS.extractAttr(this, 'wsp');
		if (this.hasAttribute('src-ref')) init.srcRef = BASIS.extractAttr(this, 'src-ref');
		if (this.hasAttribute('editor-key')) init.editorKey = BASIS.extractAttr(this, 'editor-key');
		if (this.hasAttribute('editor-config')) init.editorConfig = JSON.parse(BASIS.extractAttr(this, 'editor-config'));
		if (this.hasAttribute('wedmgr-config')) init.wedMgrConfig = JSON.parse(BASIS.extractAttr(this, 'wedmgr-config'));
		return init;
	}

	protected async launchEditor(): Promise<void> {
		let confReg = this.configs.reg as IReg<IItemUiEnv>; //On va compléter confReg pour obtenir un IReg<IItemUiEnv>
		const originalEnv = confReg.env as IItemUiEnv;

		const place = originalEnv.place || originalEnv.universe.wspServer.wspsLive.newPlace();

		let wsp = originalEnv.wsp;
		if (!wsp) {
			const chain = originalEnv.universe;
			if (!chain || !this.configs.wspCd) throw Error("Universe or wspCd not found");
			wsp = await place.getWsp(this.configs.wspCd).waitForAvailable(this);
		} else if (!wsp.isAvailable) {
			await wsp.waitForAvailable(this);
		}

		let srcRef: srcRef = this.configs.srcRef;
		if (!srcRef) {
			srcRef = ITEM.srcRefInContext(originalEnv.longDesc);
			if (originalEnv.itemType?.getFamily() === EItemTypeFamily.res) {
				//Edition des metas d'un item.
				let targetUri = originalEnv.itemType.getMainXmlSrcUri(originalEnv.longDesc);
				if (SRC.isSrcId(srcRef)) {
					//On est en contexte db hors cas spéciaux (cf srcRefInContext), on tente de récupérer l'id
					srcRef = (await WSP.fetchSrcId(originalEnv.wsp, this, targetUri, true)) || targetUri;
				} else {
					srcRef = targetUri;
				}
				//console.log(":::srcRef::::", srcRef);
			}
		}

		const docHolder = await place.newDocHolder(wsp, srcRef);
		if (!docHolder) throw Error("Source not found");

		const itemType = originalEnv.itemType || docHolder.house.itemType;

		if (confReg.env.wsp !== wsp) confReg = REG.createSubRegMixed(confReg, wsp.reg);
		if (confReg.env.itemType !== itemType) confReg = REG.createSubRegMixed(confReg, itemType.reg);

		//Reg final de niveau IItemUiEnv
		this.reg = REG.createSubReg(confReg);
		this.reg.env.place = place;
		if (!this.reg.env.longDesc) {
			const longDesc = this.reg.env.longDesc = await wsp.fetchLongDesc(srcRef);
			this.reg.env.securityCtx = SEC.createSub(wsp.reg.env.securityCtx, longDesc.srcRoles, longDesc.srcRi, longDesc);
		}

		const editorConfig = Object.assign({}, this.configs.editorConfig) as OWedEditorBoxConfig;
		const wedMgrConfig = Object.assign({}, this.configs.wedMgrConfig) as OWedManagerConfig;

		if (!wedMgrConfig.fetchContentThreshold) wedMgrConfig.fetchContentThreshold = 6000;
		if (!wedMgrConfig.dataTransferAnalyzer) wedMgrConfig.dataTransferAnalyzer = WspDataTransferWedAnalyzer.SINGLETON;

		const dataEditor = this.configs.datasEditor || itemType.getEditor(this.configs.editorKey);
		if (!dataEditor) throw Error("No editor found: " + this.configs.editorKey);

		this.view = new WedEditorBox();

		if (dataEditor.help) (await import("/@back@/edit/wed/features/helpBar.js")).initHelpOnEditor(dataEditor.help, this.reg, editorConfig, wedMgrConfig, this.view)

		if (dataEditor.initReg) await dataEditor.initReg(this.reg, this.view);
		if (dataEditor.overrideConfigs) await dataEditor.overrideConfigs(editorConfig, wedMgrConfig, this.view, this.reg, docHolder);
		if (this.configs.initItemReg) this.configs.initItemReg(this.reg, this);

		editorConfig.reg = this.reg;
		const parentDatas = this.configs.lastDatas;
		if (editorConfig.lastDatasKey === undefined) editorConfig.lastDatasKey = "ed:" + srcRef;
		const lastDatas = parentDatas && parentDatas[editorConfig.lastDatasKey];
		if (lastDatas) {
			editorConfig.lastDatas = lastDatas;
			parentDatas[editorConfig.lastDatasKey] = null; //on tue les lastDatas de cet éditeur en cas d'une fermeture et d'une réouverture dans cette session.
		}

		//chargement asynchrone en // des librairies standard wed et des wedModels
		this.view.addAsyncLib(boxTagsDefined);
		this.view.addAsyncLib(txtTagsDefined);
		this.view.addAsyncLib(ptrItemTagsDefined);
		const mainEndP = itemType.getWed(dataEditor.mainWed);
		this.view.addAsyncLib(WED.loadWedModel(mainEndP, mainEndP).then((wed) => this.view.wedModel = wed));
		if (dataEditor.outlineWed) {
			this.view.outlineWedModel = () => {
				const outlineEndP = itemType.getWed(dataEditor.outlineWed);
				return WED.loadWedModel(outlineEndP, outlineEndP);
			}
		}
		if (dataEditor.previewWed) {
			this.view.previewWedModel = () => {
				const previewEndP = itemType.getWed(dataEditor.previewWed);
				return WED.loadWedModel(previewEndP, previewEndP);
			}
		}

		//Gestion graphique du lock et de l'absence de droit en écriture
		const lockBtn = new LockAction();

		wedMgrConfig.readOnlyCauses = wedMgrConfig.readOnlyCauses ? wedMgrConfig.readOnlyCauses.concat() : [];
		if (itemType.datas.readOnly) wedMgrConfig.readOnlyCauses.push("itemType");
		if (docHolder.house.lockedBy) wedMgrConfig.readOnlyCauses.push("locked");
		else if (!this.reg.hasPerm(this._writePermToCheck())) {
			if (!this.configs.noNotifIfReadOnly) {
				if (!this.reg.hasSystemRights(this._writePermToCheck())) {
					if (!ITEM.isHistoryOrTrashUri(this.reg.env.longDesc.srcUri)) {
						POPUP.showNotifInfo("Ce contenu n'est pas éditable.", this);
					}
				} else {
					POPUP.showNotifForbidden("Vous ne disposez pas des droits pour modifier ce contenu.", this);
				}
			}
			wedMgrConfig.readOnlyCauses.push("perms");
		}
		docHolder.houseLstn.on("onLock", this.refresh.bind(this));
		docHolder.houseLstn.on("onPermChange", (house) => {
			this.reg.env.securityCtx = SEC.createSub(wsp.reg.env.securityCtx, house.srcFields.srcRoles, house.srcFields.srcRi, house.srcFields);
			this.refresh();
		});

		this._saveAct = new SaveAction(this, docHolder);
		this.reg.addToList("actions:wed:commonbar:start", "save", 1, this._saveAct, 0);
		this.reg.addToList("actions:wed:commonbar:start", "lock", 1, lockBtn);

		if (isHistoEditEnv(originalEnv) && originalEnv.infoBroker) {
			wedMgrConfig.histoEditPointsMgr = originalEnv.histoEditMgr;
			wedMgrConfig.histoEditHolder = new EditHolder(SRC.srcRef(this.reg.env.longDesc), originalEnv.infoBroker);
			const act = new HistoEditAction(originalEnv.histoEditMgr);
			this.reg.addToList("actions:wed:commonbar:start", "histoEdit", 1, act, 55);
			this.reg.addToList("accelKeys:wed:global", (navigator.platform.indexOf("Mac") >= 0 ? "l-accel-shift":"q-accel-shift"), 1, act);
		}

		this.reg.addToList("accelKeys:wed:global", "s-accel", 1, this._saveAct);
		this.reg.addToList("accelKeys:wed:global", "i-accel-alt-shift", 1, viewXmlAction);


		this.reg.addToList("actions:wed:commonbar:end", "outline", 1, new ShowOutlineAction(), 51);
		this.reg.addToList("actions:wed:commonbar:end", "preview", 1, new ShowPreviewAction(), 61);
		//this.reg.addToList("actions:wed:commonbar:end", "metaBar", 1, new ShowMetaBarAction());

		this.reg.registerSvc("wedTxtSelMgrClass", 1, WspTxtSelMgr);

		this.view.initialize(editorConfig);
		if (await this.view.initFromDocHolder(docHolder, wedMgrConfig)) {
			this.appendChild(this.view);
			place.setState(docHolder.house.wspRef, EWspWorkingSt.r); //Toujours visible.
		} else {
			this.view = null;
			this._saveAct = null;
			throw Error("Init editor failed");
		}
	}

	onViewHidden(closed?: boolean) {
		if (this.view && this.reg) (this.reg.env as IHistoEditEnv).histoEditMgr?.onEditorLooseFocus(this.view.wedMgr.config.histoEditHolder);
		if (closed) this.closeEditor();
		else VIEWS.onViewHidden(this.view, closed);
	}

	visitViews(visitor: (view: IView) => any): any {
		if (this.view) return visitor(this.view);
	}

	visitViewsAsync(visitor: (view: IView) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		return this.view ? visitor(this.view) : Promise.resolve(undefined);
	}

}

customElements.define("wsp-item-xmled", ItemXmlEd);


class SaveAction extends Action<IWedEditor> {

	saving: boolean;

	button: ActionBtn<IWedEditor>;

	constructor(readonly itemEditor: ItemXmlEd, docHolder: IPlaceDocHolder) {
		super('save');
		this.setIcon("/@skin@/edit/wed/save.svg");
		this.setLabel("Enregistrer");
		docHolder.houseLstn.on('dirtyChange', () => {if (this.button) this.button.refresh()});
		docHolder.houseLstn.on('onSave', (state: 'saving' | 'saved' | 'saveFailed', e?: any): void | Promise<void> => {
			if (!this.button) return;
			this.button.classList.toggle('saving', state === 'saving');
			if (state === 'saveFailed') {
				if (e instanceof RespError && e.response.status === EHttpStatusCode.forbidden) {
					const wedMgr = itemEditor.view.wedMgr;
					wedMgr.writeRangeToClipboard(XA.newRangeAround(wedMgr.rootWedlet.wedAnchor)).then(() => {
						POPUP.showNotifForbidden("Vous ne disposez plus des droits en édition, vos modifications annulées ont été copiées dans le presse-papier.", itemEditor, {autoHide: 20000});
					}).catch(() => {
						POPUP.showNotifForbidden("Vous ne disposez plus des droits en édition, vos modifications ont dû être annulées.", itemEditor, {autoHide: 20000});
					});
					return docHolder.place.abortChangesAndReload(docHolder.house);
				} else {
					POPUP.showNotifError("Échec à l'enregistrement, connexion au serveur impossible. Attention, vous risquez de perdre vos modifications.", itemEditor);
				}
				this.saving = false; //pour que le refresh du bouton prenne en compte l'état dirty dû à l'échec cf isEnabled(ctx: IWedEditor)
			}
		})
	}

	/** Pas de bouton save pour les sous-editeurs. */
	buildCustomButton(ctx: IWedEditor, uiContext: EButtonUiContext, parent?: Element): Element | null | undefined {
		return this.button ? null : undefined;
	}

	initButtonNode(buttonNode: ActionBtn<IWedEditor>, ctx: IWedEditor) {
		this.button = buttonNode;
		this.itemEditor.reg.installSkin("wsp-item-xmled/saveBtn", buttonNode.shadowRoot);
	}

	isVisible(ctx: IWedEditor): boolean {
		if (ctx.wedMgr.readOnly) return false;
		return super.isVisible(ctx);
	}

	isEnabled(ctx: IWedEditor): boolean {
		if (this.saving) return false;
		return super.isEnabled(ctx) && ctx.wedMgr.docHolder.house.isDirty;
	}

	async execute(ctx: IWedEditor, ev?: Event) {
		const docHolder = ctx.wedMgr.docHolder as IPlaceDocHolder;
		if (!docHolder.save) return; //Pas un IPlaceDocHolder : cas d'un DocHolder local détaché de l'item : metas en création => abandon ddu ctrl+S.
		try {
			this.saving = true;
			await docHolder.save();
		} finally {
			this.saving = false;
		}
	}
}

REG.reg.registerSkin("wsp-item-xmled/saveBtn", 1, /* language=CSS */ `
	:host(.saving) {
		--icon-color: red;
	}
`);


class LockAction extends Action<IWedEditor> {

	constructor() {
		super('lock');
		this.setIcon("/@skin@/wsp/widgets/itemEditor/editing.svg");
		this.setDescription("Ce contenu n'est pas éditable car il est actuellement en cours d'édition.");
	}

	isVisible(ctx: IWedEditor): boolean {
		return this.getLockedBy(ctx) != null;
	}

	getLabel(ctx: IWedEditor) {
		const lock = this.getLockedBy(ctx);
		return lock ? "Édité par " + lock.account : null;
	}

	initButtonNode(buttonNode: ActionBtn<IWedEditor>, ctx: IWedEditor) {
		buttonNode._hideLabel = false;
		buttonNode.tabIndexCustom = -2;
	}

	getLockedBy(ctx: IWedEditor) {
		return (ctx.wedMgr.docHolder as IPlaceDocHolder).house.lockedBy;
	}
}

const viewXmlAction = new Action<IWedEditor>("viewXml")
	.setExecute(
		(ctx: IWedEditor, ev?: Event): any => {
			POPUP.showDialog(
				new ItemXmlEd().initialize({
					reg: ctx.reg as IReg<IItemUiEnv>,
					datasEditor: {mainWed: "rawXml"},
					wedMgrConfig: {
						//readOnlyCauses: ["rawXml"],
						noCleanup: true
					},
					noNotifIfReadOnly: true
				}), ctx, {initHeight: "90%", initWidth: "90%", titleBar: {barLabel: {label: "Source XML"}}, resizer: {}});
		});

REG.reg.addToList("actions:wed:box", "scComment", 1, new InsertScComment("scComment"));

REG.reg.registerSvc("wedItemBlock", 1, SRCDRAWER.previewAndItemCodeAndTitleTpl);
REG.reg.registerSvc("wedItemBlockCompact", 1, SRCDRAWER.itemCodeAndTitleTpl);
REG.reg.registerSvc("wedItemBlockOver", 1, SRCDRAWER.detailsOrTypesTpl);
REG.reg.registerSvc("wedItemBlockOverCompact", 1, SRCDRAWER.detailsPreviewOrTypesTpl);
REG.reg.registerSvc("wedItemInline", 1, SRCDRAWER.itemTitleOrCodeTpl);
REG.reg.registerSvc("wedItemInlineOver", 1, SRCDRAWER.detailsPreviewOrTypesTpl);

REG.reg.registerSvc("wedItemInlULink", 1, function wedItemInlULink(this: TxtElement) {
	if (!(this as any)._wedItemInlULinkInited) {
		(this as any)._wedItemInlULinkInited = true;
		POPUP.promiseTooltip(this, (owner: HTMLElement, tooltip: PopupTooltip): IView | null => {
				const reg = this.wedMgr.reg;
				const infoBroker = reg.env.infoBroker;

				const btnShow = infoBroker ? new Button().initialize({reg, label: "Afficher l'item lié", icon: "/@skin@/wsp/actions/showItem.svg", uiContext: "dialog"}) : undefined;
				if (btnShow) btnShow.onclick = () => {
					const srcRef = this.wedAttributes["sc:refUri"] as srcRef;
					if (srcRef) {
						const subItemId = ITEM.extractSubItemId(srcRef);
						if (subItemId) {
							infoBroker.dispatchInfo(new InfoFocusNodeInItem(`//*[@xml:id='${subItemId}']`, ITEM.extractSrcRef(srcRef, subItemId)), this);
						} else {
							infoBroker.dispatchInfo(new InfoFocusItem(srcRef), this);
						}
						tooltip.close();
					}
				};

				const btnEdit = WEDLET.isWritableWedlet(this) ? new Button().initialize({reg, label: "Changer d'item lié", icon: "/@skin@/wsp/actions/fastFind.svg", uiContext: "dialog"}) : undefined;
				if (btnEdit) btnEdit.onclick = async (ev: MouseEvent) => {
					tooltip.close();
					//Chargement asynch du fastFind
					const {FastFindItem} = await import("/@back@/wsp/dialogs/fastFindItem.js");
					const sgnPattern = sgnPatternFromTxtElement(this);
					const subSgnPattern = sgnPatternFromTxtElement(this, true);

					const fastFindItemMk = reg.getSvc("fastFindItemMk") as (init: OFastFindItemsInit) => FastFindItem | null;
					if (fastFindItemMk) {
						const ct = fastFindItemMk({
							reg,
							restrictions: sgnPattern ? new SearchItemSgnRegexp(sgnPattern.source) : null,
							subItemsFind: subSgnPattern ? {subItemSgnPattern: subSgnPattern} : undefined
						});
						if (ct) {
							const sd = await POPUP.showMenuFromEvent<JSrcFields | null>(ct, ev, this, null, {initMaxHeight: '50vh'}).onNextClose();
							if (sd && this.isConnected) {
								this.wedMgr.docHolder.newBatch().setAttr(XA.append(this.wedAnchor, "sc:refUri"), ITEM.srcRefSub(sd)).doBatch();
							}
						}
					}
				};

				const srcDrawer = new SrcDrawer().initialize({reg, skin: 'wsp-src-drawer/tooltip', tpl: SRCDRAWER.detailsItemIconPreviewTpl});
				tooltip.addEventListener('c-show', (ev: CustomEvent) => {
					const srcRef = this.wedAttributes["sc:refUri"] as srcRef;
					if (srcRef) srcDrawer.setSrcRef(srcRef, null);//Pas de listener, on force le rechargement du srcRef
					else ev.preventDefault();
				});
				return <div>{btnShow}{btnEdit}{srcDrawer}</div>;
			},
			{skinOver: 'wsp-src-drawer/tooltip', hoverAllowed: true, anchor: {posFrom: this, fromX: 'end', marginX: -1, notAvailableSpace: {posFrom: this, fromX: 'start', targetX: 'end', marginX: 1}}}
		);
	}
} as ITxtRefresh);

REG.reg.registerSvc("wedItemInlImg", 1, function wedItemInlULink(this: TxtElement) {
	let ch = DOM.findFirstChild(this, (n): n is TxtPtrItemInline => n instanceof TxtPtrItemInline);
	if (!ch) {
		const reg = REG.findReg<IWspUiEnv>(this.wedlet.wedMgr.wedEditor);
		ch = this.appendChild(new TxtPtrItemInline().initialize({
			reg: reg,
			place: reg.env.place,
			dispatchHighlight: true,
			tpl: reg.getSvc("wedItemInlImgTpl") || SRCDRAWER.previewOrItemTitleOrCodeTpl,
			tplOver: reg.getSvc("wedItemInlImgTplOver", SRCDRAWER.detailsOrTypesTpl)
		}));
		this.appendChild(newEditLinkBtn());
	}
	(ch as TxtPtrItem).initSrcRef(this.wedAttributes["sc:refUri"]);
} as ITxtRefresh);

function newEditLinkBtn() {
	return <span class="editLnk" onclick={function (this: HTMLSpanElement, ev: MouseEvent) {
		const ptrItem = this.previousSibling as TxtPtrItemInline;
		ptrItem?.editAction?.executeIfAvailable(ptrItem, ev)
	}}/>;
}

REG.reg.registerSvc("wedItemInlObject", 1, function wedItemInlObject(this: TxtElement) {
	let ch = DOM.findFirstChild(this, (n): n is TxtPtrItemInline => n instanceof TxtPtrItemInline);
	if (!ch) {
		const reg = REG.findReg<IWspUiEnv>(this.wedlet.wedMgr.wedEditor);
		ch = this.appendChild(new TxtPtrItemInline().initialize({
			reg: reg,
			place: reg.env.place,
			dispatchHighlight: true,
			tpl: reg.getSvc("wedItemInlObjectTpl") || SRCDRAWER.itemTitleOrCodeTpl,
			tplOver: reg.getSvc("wedItemInlObjectTplOver", SRCDRAWER.detailsPreviewOrTypesTpl)
		}));
		this.appendChild(newEditLinkBtn());
	}
	(ch as TxtPtrItem).initSrcRef(this.wedAttributes["sc:refUri"]);
} as ITxtRefresh);

REG.reg.registerSvc("wedItemTxtObject", 1, function wedItemTxtObject(this: TxtElement) {
	let ch = DOM.findFirstChild(this, (n): n is TxtPtrItemBlock => n instanceof TxtPtrItemBlock);
	if (!ch) {
		const reg = REG.findReg<IWspUiEnv>(this.wedlet.wedMgr.wedEditor);
		ch = this.appendChild(new TxtPtrItemBlock().initialize({
			reg: reg,
			place: reg.env.place,
			dispatchHighlight: true,
			tpl: reg.getSvc("wedItemTxtObjectTpl") || SRCDRAWER.previewAndItemCodeAndTitleTpl,
			tplOver: reg.getSvc("wedItemTxtObjectTplOver", SRCDRAWER.detailsOrTypesTpl)
		}));
		this.appendChild(newEditLinkBtn());
	}
	ch.initSrcRef(this.wedAttributes["sc:refUri"]);
} as ITxtRefresh);

REG.reg.registerSvc("wedItemTxtObjectDiff", 1, {
	addDiff(elt: TxtObject, annot: ISkDiffAnnot) {
		//console.log("wedItemTxtObjectDiff:::addDiff::", annot);
		if (!elt.wedAnnotDiff) new WEDLET.diffLib.WedDiffTxtObj().initDiffTxtObj(elt, annot, {
			redrawDiff(diffTxtObj: ItemWedDiffTxtObj) {
				//Refresh du widget de diff
				const srcDiff = diffTxtObj.skAnnots.find((d) => d.type === "diffValue" && d.start[diffTxtObj.txtObjDepth] === "sc:refUri") as IDiffAnnotValue;
				if (srcDiff) {
					//On a une modif de la src de l'item
					if (!diffTxtObj.foreignPtrItem) {
						const reg = diffTxtObj.txtObj.wedMgr.reg;
						const tag = new TxtPtrItemBlock().initialize({
							reg: reg,
							place: reg.env.place,
							dispatchHighlight: true,
							tpl: reg.getSvc("wedItemTxtObjectTpl") || SRCDRAWER.previewAndItemCodeAndTitleTpl,
							tplOver: reg.getSvc("wedItemTxtObjectTplOver", SRCDRAWER.detailsOrTypesTpl)
						});
						tag.initSrcRef(srcDiff.otherValue);
						diffTxtObj.foreignPtrItem = <div style="background-color:var(--edit-diff-bgcolor)">{tag}</div>;
						diffTxtObj.txtObj.prepend(diffTxtObj.foreignPtrItem);
					}
				} else if (diffTxtObj.foreignPtrItem) {
					//Pas de modif de la src, on clean on avait un widget
					diffTxtObj.txtObj.removeChild(diffTxtObj.foreignPtrItem);
					diffTxtObj.foreignPtrItem = null;
				}
				//Si au moins une diff dans les metas, on ajoute une class diffInMeta
				diffTxtObj.txtObj.classList.toggle("diffInMeta", diffTxtObj.skAnnots.length > (srcDiff ? 1 : 0));
			}
		} as IWedDiffTxtObjCustom);
		else elt.wedAnnotDiff.addAnnotForMetas(annot);
	},
	removeDiff(elt: TxtObject, annot: ISkDiffAnnot) {
		//console.log("wedItemTxtObjectDiff:::removeDiff::", annot);
		if (elt.wedAnnotDiff) elt.wedAnnotDiff.removeAnnotForMetas(annot);
	}
} as ITxtAnnotDiff);

interface ItemWedDiffTxtObj extends WedDiffTxtObj {
	foreignPtrItem: HTMLElement
}


REG.reg.registerSvc("wedItemTxtEmptyDiff", 1, {
	addDiff(elt: TxtObject, annot: ISkDiffAnnot) {
		//console.log("wedItemTxtObjectDiff:::addDiff::", annot);
		if (!elt.wedAnnotDiff) new WEDLET.diffLib.WedDiffTxtObj().initDiffTxtObj(elt, annot, {
			redrawDiff(diffTxtObj: ItemWedDiffTxtObj) {
				diffTxtObj.txtObj.classList.toggle("diffInMeta", diffTxtObj.skAnnots.length > 0);
			}
		} as IWedDiffTxtObjCustom);
		else elt.wedAnnotDiff.addAnnotForMetas(annot);
	},
	removeDiff(elt: TxtObject, annot: ISkDiffAnnot) {
		//console.log("wedItemTxtObjectDiff:::removeDiff::", annot);
		if (elt.wedAnnotDiff) elt.wedAnnotDiff.removeAnnotForMetas(annot);
	}
} as ITxtAnnotDiff);

/** Service d'externalisation d'un contenu vers un item. */
REG.reg.registerSvc<(from: IReg<IWspEnv>, content: Element, itTypesReducer: (acc: ItemType[], cur: ItemType, idx?: number, src?: ItemType[]) => ItemType[], uiCtx?: HTMLElement) => Promise<JSrcFields | null>>("wedExternalizeItem", 1,
	async function (from: IReg<IWspEnv>, content: Element, itTypesReducer: (acc: ItemType[], cur: ItemType, idx?: number, src?: ItemType[]) => ItemType[], uiCtx?: HTMLElement): Promise<JSrcFields | null> {
		const {ItemCreator} = await import("/@back@/wsp/dialogs/itemCreator.js");
		const itemReg = REG.findReg(uiCtx);
		const root = DOM.newDomDoc().createElementNS(DOM.SCCORE_NS, DOM.SCITEM_TAG);
		root.appendChild(root.ownerDocument.importNode(content, true));
		return ItemCreator.openCreateItem(ItemCreator.setDefaultConfigFromReg(from, {
			body: new Blob([DOM.ser(root, true)], {type: 'text/xml'}),
			itemTypesTree: {itemTypeReducer: itTypesReducer},
			spaceUri: isItemUiEnv(itemReg.env) ? ITEM.extractSpaceUri(itemReg.env.longDesc.srcUri) : null
		}), "Externaliser le contenu vers un item...", uiCtx).onNextClose();
	});


/** InfoBorker qui capte l'ouverture d'un item pour afficher son éditeur par défaut dans un dialogue "cul de sac". */
export class ItemEditInDialogInfoBroker extends InfoBrokerBasic {

	constructor(public reg: IReg<IWspUiEnv>) {
		super();
	}

	async dispatchInfo(info: IInfo, from: IInfoProducer) {
		//console.log(info, from);

		if (from instanceof Node && !from.isConnected) return;

		if (info instanceof InfoFocusItem) {
			const shortDesc = info.shortDesc || await this.reg.env.wsp.fetchShortDesc(info.srcRef, this.reg.env.uiRoot);
			const itemType = this.reg.env.wsp.wspMetaUi.getItemType(shortDesc.itModel);

			const subReg = REG.createSubRegMixed(this.reg, itemType.reg);
			subReg.env.infoBroker = new InfoBrokerBasic();

			const itemViewer = new ItemViewerSingle({mainViewCode: ":hideWspStruct:"});
			const root = <ViewsContainer î={{views: [itemViewer]}} style="flex:1;display:flex;min-height:0;min-width:0;flex-direction:column;"/> as ViewsContainer;
			await itemViewer.initViewer(subReg, root, null, info.srcRef);

			if (info instanceof InfoFocusNodeInItem) {
				subReg.env.infoBroker.dispatchInfo(new InfoFocusNodeInItem(info.xpath, info.srcRef), this);
			}

			const popup = POPUP.findPopupableParent(from) as IPopupable & IInfoBrokerPointer;
			if (popup && popup.infoBroker === this) {
				//Reéntrance dans le popup.
				const currentEd = popup.firstElementChild as ItemXmlEd;
				if (currentEd.configs.srcRef === info.srcRef) return; //XXX Pb résolution des refs ?
				VIEWS.onViewHidden(currentEd, true);
				popup.firstElementChild.remove();
				popup.appendChild(root);
			} else {
				const newPopup = POPUP.showDialog(root, this.reg.env.uiRoot, {
					titleBar: {barLabel: {label: "Sous-item"}},
					initWidth: '90%',
					initHeight: '90%',
					resizer: {}
				}) as IInfoBrokerPointer;
				newPopup.infoBroker = this; //Pour la réentrance
			}
			info.handled = true;
			return;
		}
		super.dispatchInfo(info, from);
	}
}


class WspTxtSelMgr extends TxtSelMgr {

	protected _infoBroker: IInfoBroker;

	protected _highlightForLink: InlLink;
	protected _highlightSgn: RegExp;

	constructor(root: TxtRoot) {
		super(root);
		//si accès synchrone, on va pouvoir dispatcher l'info InfoHighlighItemSgn en fonction de la sel.
		if (this._root.wedMgr.docHolder) this._infoBroker = this._root.wedMgr.reg.env.infoBroker;
	}

	onBlur(ev: FocusEvent) {
		super.onBlur(ev);
		if (this._highlightForLink) {
			this._highlightForLink = null;
			if (this._highlightSgn) {
				this._highlightSgn = null;
				this._infoBroker.dispatchInfo(new InfoHighlighItemSgn(null), this);
			}
		}
	}

	onSelChange() {
		super.onSelChange();
		if (this._sel.focusNode && this._infoBroker) {
			//Highlight des items compatibles avec l'inlLink courant.
			const inlLink = DOM.findParent(this._oldSel.commonAncestorContainer, this._root, (n: Node): n is InlLink => n instanceof InlLink);
			if (this._highlightForLink !== inlLink) {
				this._highlightForLink = inlLink;
				let sgn: RegExp = null;
				let assigned: srcRef;
				if (inlLink) {
					const path = inlLink.wedAnchor;
					path.push("sc:refUri");
					assigned = inlLink.wedAttributes && inlLink.wedAttributes["sc:refUri"];
					const struct = this._root.wedMgr.docHolder.getStruct(path, assigned !== undefined ? null : ["@"]);
					//console.log("struct.itSgnPattern::::", struct ? (struct as IPtrItemRule).itSgnPattern : null);
					sgn = struct && (struct as IPtrItemRule).itSgnPattern;
				}
				if (this._highlightSgn !== sgn) {
					this._highlightSgn = sgn;
					this._infoBroker.dispatchInfo(new InfoHighlighItemSgn(sgn, assigned), this);
				}
			}
		}
	}
}

class EditHolder implements IHistoEditHolder {
	constructor(public srcRef: srcRef, public infoBroker: IInfoBroker) {}

	isSame(other: IHistoEditHolder): boolean {
		return (other as EditHolder).srcRef === this.srcRef;
	}

	goTo(targetAddr: HistoEditPoint): boolean {
		this.infoBroker.dispatchInfo(new InfoFocusNodeInItem(targetAddr.addr, this.srcRef), this);
		return true;
	}
}